import React, { useState } from 'react';
import { Tv, MapPin, Rocket, ArrowRight, Play, Clock } from 'lucide-react';
import type { PublisherConfig } from '../../types/selfService';
import { displayedName } from '../../data/publisherConfigs';
import { accentVars, tintFromAccent } from './steps/_shared';
import { AdvertiserFaqList } from './help/advertiserFaq';
import { resolveStationDevices, type StreamingDeviceDisplay } from './streamingDeviceDisplay';

interface SelfServiceLandingProps {
  config: PublisherConfig;
  onGetStarted: () => void;
}

const STEPS = [
  { icon: Tv, title: 'Add Business' },
  { icon: MapPin, title: 'Set Targeting' },
  { icon: Play, title: 'Upload Ad' },
  { icon: Rocket, title: 'Launch' },
];

// CTV-567 dynamic page sizing. The TV hero is the tallest thing on the landing;
// left at its natural width it pushes the "How it works" tracker below the fold
// on short laptops. We cap the hero's WIDTH by the available viewport height so
// it shrinks on short screens instead: its height follows via the natural
// aspect ratio, which keeps the percentage-positioned screen overlay aligned.
// CTV-675: the ratio is also declared to the layout engine (aspect-ratio on the
// hero wrapper + width/height on the img) so the box is reserved before the
// image bytes arrive and the page never jumps. `svh` (not `dvh`) so the hero
// does not resize when the mobile URL bar collapses.
// HERO_RESERVED_PX is the vertical budget (header + CTA + "How it works" + gaps)
// that must share the viewport with the TV; tune it if those sections change.
const TV_NATURAL_W = 1932; // natural pixel size of the TV chrome image
const TV_NATURAL_H = 1150;
const TV_ASPECT = TV_NATURAL_W / TV_NATURAL_H;
// Because the hero's aspect ratio is fixed, its rendered height === (100svh -
// HERO_RESERVED_PX). Reserve just enough for the header + CTA + "How it works"
// tracker (+ a little slack) so the TV stays as large as possible while keeping
// the tracker above the fold. At 720px tall this leaves ~50px below the tracker.
const HERO_RESERVED_PX = 360;
const HERO_MAX_WIDTH = `min(100%, calc((100svh - ${HERO_RESERVED_PX}px) * ${TV_ASPECT}))`;

const SelfServiceLanding: React.FC<SelfServiceLandingProps> = ({ config, onGetStarted }) => {
  const accent = config.accentColor || config.primaryColor;
  const primary = config.primaryColor || accent;
  const label = displayedName(config);

  // Only pause for the video's clean-intro window when there's actually a video.
  const pauseSec = config.backgroundVideoUrl ? VIDEO_PAUSE_SEC : 0;
  const cp = makeCp(pauseSec);
  const cycleSec = CONTENT_SEC + pauseSec;

  return (
    <div className="relative" style={accentVars(accent, config.primaryColor)}>
      <FaceliftKeyframes cp={cp} />

      {/* Aurora backdrop — bleeds edge-to-edge behind hero */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[850px] overflow-hidden"
        style={{ left: 'calc(-50vw + 50%)', right: 'calc(-50vw + 50%)' }}
      >
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl opacity-70"
          style={{
            background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.22)}, transparent 70%)`,
          }}
        />
        <div
          className="absolute top-24 left-[8%] w-[420px] h-[420px] rounded-full blur-3xl opacity-60"
          style={{
            background: `radial-gradient(closest-side, ${tintFromAccent(primary, 0.18)}, transparent 70%)`,
          }}
        />
        <div
          className="absolute top-40 right-[6%] w-[360px] h-[360px] rounded-full blur-3xl opacity-70"
          style={{
            background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.16)}, transparent 70%)`,
          }}
        />
      </div>

      <div className="relative max-w-4xl mx-auto">
        {/* Smart TV mockup — centered */}
        <div className="relative w-full pt-2 pb-2 px-1">
          <TvMockup
            accent={accent}
            primary={primary}
            cp={cp}
            cycleSec={cycleSec}
            logoUrl={config.logoUrl}
            logoAlt={`${label} logo`}
            backgroundVideoUrl={config.backgroundVideoUrl ?? null}
            headlineLines={['Your business', `on ${label}`]}
            devices={resolveStationDevices(config.devices)}
            subtitle="Connect with your customers on streaming TV"
          />
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center justify-center gap-2.5 pb-5 px-1">
          <div className="relative">
            {/* Pulsing aura behind the button */}
            <span
              aria-hidden
              className="absolute inset-0 -m-4 rounded-full pointer-events-none"
              style={{
                background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.45)}, transparent 70%)`,
                animation: 'ssl-cta-pulse 2.8s ease-in-out infinite',
              }}
            />
            <button
              type="button"
              onClick={onGetStarted}
              className="group relative inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-full text-[16px] font-semibold tracking-tight text-white transition-all hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] overflow-hidden"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${primary})`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.3) inset, 0 0 0 1px rgba(255,255,255,0.12) inset, 0 20px 44px -12px ${primary}, 0 8px 22px -8px ${tintFromAccent(accent, 0.6)}`,
              }}
            >
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full"
                style={{
                  background:
                    'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)',
                  animation: 'ssl-shimmer 5.5s ease-in-out infinite',
                }}
              />
              <span className="relative inline-flex items-center gap-2.5">
                Get Started
                <ArrowRight className="w-[18px] h-[18px] transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 uppercase tracking-[0.1em]">
            <Clock className="w-3 h-3" />
            Takes about 5 minutes
          </span>
        </div>

        {/* How it works — horizontal step tracker */}
        <div className="mb-6 max-w-3xl mx-auto px-1">
          <div
            className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] mb-4"
            style={{ color: accent }}
          >
            How it works
          </div>
          <div className="relative grid grid-cols-4 gap-2">
            {/* Connector line behind the dots */}
            <div
              aria-hidden
              className="absolute top-7 h-px"
              style={{
                left: '12.5%',
                right: '12.5%',
                backgroundImage: `linear-gradient(90deg, ${tintFromAccent(accent, 0.4)} 50%, transparent 50%)`,
                backgroundSize: '8px 1px',
                animation: 'ssl-march 1.6s linear infinite',
              }}
            />
            {STEPS.map((step, i) => (
              <div key={step.title} className="relative flex flex-col items-center text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center bg-white shrink-0 z-10 relative"
                  style={{
                    border: `1px solid ${tintFromAccent(accent, 0.35)}`,
                    color: accent,
                    boxShadow: `0 1px 0 rgba(255,255,255,0.9) inset, 0 8px 24px -10px ${tintFromAccent(primary, 0.55)}`,
                  }}
                >
                  <step.icon className="w-5 h-5" />
                  <div
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{
                      background: `linear-gradient(135deg, ${accent}, ${primary})`,
                      boxShadow: `0 2px 6px -2px ${tintFromAccent(primary, 0.6)}`,
                    }}
                  >
                    {i + 1}
                  </div>
                </div>
                <div className="text-[12px] font-semibold tracking-tight text-slate-900 mt-3 leading-snug">
                  {step.title}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-14 max-w-3xl mx-auto px-1">
          <h2
            className="text-center text-[18px] md:text-[20px] font-semibold tracking-tight mb-5"
            style={{ color: accent }}
          >
            FAQs
          </h2>
          <AdvertiserFaqList accentColor={accent} publisherName={label} devices={config.devices} />
        </div>
      </div>
    </div>
  );
};

// TV screen timing. The slide choreography plays over CONTENT_SEC. When a
// station has a rendered background video, its first VIDEO_PAUSE_SEC play clean
// before the baked treatment fades in, so the text waits that long to match
// (the pause leads every cycle) and the full period is CONTENT_SEC +
// VIDEO_PAUSE_SEC — LOCKED to the video's length (TARGET_DURATION_SEC in
// selfservice-api background-video.util.ts) so the two loops never drift.
//
// With ONLY the gradient (no video) there's nothing to hold clean for, so the
// pause is 0: the text comes in immediately and the period is just CONTENT_SEC.
const CONTENT_SEC = 21;
const VIDEO_PAUSE_SEC = 3; // keep CONTENT_SEC + VIDEO_PAUSE_SEC === TARGET_DURATION_SEC
type Cp = (contentPct: number) => string;
// Map a % of the CONTENT_SEC slide timeline onto the full cycle (CONTENT_SEC +
// pauseSec), shifted so the pause leads each loop. Every TV keyframe stop is a %
// of the slide timeline passed through cp(); the region before cp(0) is the
// video-only pause (empty when pauseSec === 0).
const makeCp = (pauseSec: number): Cp => (contentPct) =>
  (((pauseSec + (contentPct / 100) * CONTENT_SEC) / (CONTENT_SEC + pauseSec)) * 100).toFixed(2);

const FaceliftKeyframes: React.FC<{ cp: Cp }> = ({ cp }) => (
  <style>{`
    @keyframes ssl-shimmer {
      0% { transform: translateX(-100%); }
      60% { transform: translateX(100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes ssl-cta-pulse {
      0%, 100% { transform: scale(1); opacity: 0.55; }
      50%      { transform: scale(1.18); opacity: 0.85; }
    }
    @keyframes ssl-march {
      from { background-position: 0 0; }
      to { background-position: 16px 0; }
    }
    @keyframes ssl-march-v {
      from { background-position: 0 0; }
      to { background-position: 0 16px; }
    }
    @keyframes ssl-pulse-dot {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.1); }
    }
    @keyframes ssl-underline-punch {
      0%, ${cp(9)}%   { opacity: 1; transform: scaleX(0); }
      ${cp(11)}%      { opacity: 1; transform: scaleX(1); }
      ${cp(28)}%      { opacity: 1; transform: scaleX(1); }
      ${cp(33)}%      { opacity: 0; transform: scaleX(1); }
      100%     { opacity: 0; transform: scaleX(0); }
    }
    @keyframes ssl-tv-slide-2 {
      0%, ${cp(35)}% { opacity: 0; transform: translateY(6px) scale(0.98); }
      ${cp(38)}%, ${cp(58)}% { opacity: 1; transform: translateY(0) scale(1); }
      ${cp(61)}%, 100% { opacity: 0; transform: translateY(6px) scale(0.98); }
    }
    @keyframes ssl-chip-pop {
      0%, ${cp(36)}%   { opacity: 0; transform: scale(0.55) translateY(10px); }
      ${cp(39)}%       { opacity: 1; transform: scale(1.12) translateY(-2px); }
      ${cp(42)}%       { opacity: 1; transform: scale(1) translateY(0); }
      ${cp(56)}%       { opacity: 1; transform: scale(1) translateY(0); }
      ${cp(60)}%       { opacity: 0; transform: scale(0.95) translateY(-4px); }
      100%      { opacity: 0; transform: scale(0.95) translateY(-4px); }
    }
    @keyframes ssl-tv-slide-3 {
      0%, ${cp(63)}% { opacity: 0; transform: translateY(6px) scale(0.98); }
      ${cp(66)}%, ${cp(89)}% { opacity: 1; transform: translateY(0) scale(1); }
      ${cp(92)}%, 100% { opacity: 0; transform: translateY(6px) scale(0.98); }
    }
    @keyframes ssl-slide3-word {
      0%, ${cp(1)}% { opacity: 0; transform: translateY(12px) scale(0.94); }
      ${cp(6)}%     { opacity: 1; transform: translateY(-2px) scale(1.02); }
      ${cp(10)}%    { opacity: 1; transform: translateY(0) scale(1); }
      ${cp(82)}%    { opacity: 1; transform: translateY(0) scale(1); }
      ${cp(88)}%, 100% { opacity: 0; transform: translateY(-4px) scale(0.96); }
    }
    /* Channel bug grows out of nothing in the bottom-right corner once the
       channel-name line has landed (~11%): it scales up from 0, overshoots
       slightly past its final size, then eases back down to rest in a single
       settle — one overshoot, no oscillation/jiggle. Holds through the
       pitch slides, then fades off before the loop restarts so it stamps in
       fresh each cycle. Origin is bottom-right (set on the element) so the
       scale-from-0 expands out of the corner and never overflows the
       right/bottom edges; the motion is logo-size-agnostic. The whole grow +
       overshoot + settle is ONE continuous segment (11%→19%) driven by an
       easeOutBack curve: it springs up past final size mid-flight then rolls
       smoothly back to rest. The peak is just a turnaround point — never a
       keyframed stop — so it never sits at the overshoot size. */
    @keyframes ssl-bug-stamp {
      0%, ${cp(10)}%   { opacity: 0; transform: scale(0); }
      ${cp(11)}%       { opacity: 1; transform: scale(0); animation-timing-function: cubic-bezier(0.34, 1.45, 0.64, 1); }
      ${cp(19)}%       { opacity: 0.95; transform: scale(1); }
      ${cp(90)}%       { opacity: 0.95; transform: scale(1); }
      ${cp(96)}%, 100% { opacity: 0; transform: scale(1); }
    }
  `}</style>
);

interface TvMockupProps {
  accent: string;
  primary: string;
  // Timing, derived from whether the station has a video (see SelfServiceLanding).
  cp: Cp;
  cycleSec: number;
  headlineLines: string[];
  subtitle: string;
  // CTV-810: the station's selected devices (resolved via
  // resolveStationDevices, so an empty selection already fell back to the
  // full canonical set).
  devices: StreamingDeviceDisplay[];
  logoUrl?: string;
  logoAlt?: string;
  // CTV-610: per-station rendered background video. When null/absent the TV
  // screen shows only the accent gradient (the fallback).
  backgroundVideoUrl?: string | null;
}

const TvMockup: React.FC<TvMockupProps> = ({ accent, primary, cp, cycleSec, headlineLines, subtitle, devices, logoUrl, logoAlt, backgroundVideoUrl }) => {
  const INTRA_STAGGER_SEC = 0.18;
  const LINE_PAUSE_SEC = 1.0;

  // CTV-810: the device pills share one label size per row, scaled down from
  // the 1.7cqw default only when the row's longest label would wrap inside
  // the 12.4cqw pill. 1.7cqw fits about 8 characters ("Apple TV"); longer
  // labels ("Samsung TV") shrink the whole row evenly so icons and text stay
  // aligned on a single line.
  const longestLabel = devices.reduce((n, d) => Math.max(n, d.label.length), 0);
  const deviceLabelCqw =
    longestLabel > 8 ? Math.max(1.15, Math.round((13.6 / longestLabel) * 100) / 100) : 1.7;

  // CTV-675: the background video is the slowest-loading part of the hero, so
  // it fades in over the station gradient instead of popping.
  const [videoReady, setVideoReady] = useState(false);

  // A single all-caps word on its own line (e.g. "LIVE") is treated as the
  // dramatic punch — it gets the scale-snap entrance and the underline.
  const lineMeta = headlineLines.map((line) => {
    const words = line.split(' ');
    const isImpactLine =
      words.length === 1 && words[0].length > 1 && words[0] === words[0].toUpperCase();
    return { words, isImpactLine };
  });

  // Pre-compute per-word delay percentages so all animations share a single
  // synchronized 12s cycle. Stagger is baked into the keyframes, not into
  // animation-delay — otherwise each word loops on its own clock and they
  // drift apart, leaving the subtitle still on screen when the headline
  // restarts.
  const wordMeta: { popStartPct: number; isImpact: boolean }[] = [];
  {
    let gi = 0;
    lineMeta.forEach((lm, lineIdx) => {
      lm.words.forEach(() => {
        const delaySec = gi * INTRA_STAGGER_SEC + lineIdx * LINE_PAUSE_SEC;
        wordMeta.push({
          popStartPct: (delaySec / CONTENT_SEC) * 100,
          isImpact: lm.isImpactLine,
        });
        gi++;
      });
    });
  }

  const HOLD_END_PCT = 28;
  const FADE_END_PCT = 33;
  const wordKeyframesCss = wordMeta.map((m, i) => {
    const s = m.popStartPct;
    if (m.isImpact) {
      return `@keyframes ssl-w-${i} {
        0%, ${cp(s)}% { opacity: 0; transform: scale(1.55) translateY(20px); }
        ${cp(s + 3)}% { opacity: 1; transform: scale(0.84) translateY(0); }
        ${cp(s + 6)}% { opacity: 1; transform: scale(1.04) translateY(0); }
        ${cp(s + 10)}% { opacity: 1; transform: scale(1) translateY(0); }
        ${cp(HOLD_END_PCT)}% { opacity: 1; transform: scale(1) translateY(0); }
        ${cp(FADE_END_PCT)}% { opacity: 0; transform: scale(1) translateY(-6px); }
        100% { opacity: 0; transform: scale(1) translateY(-6px); }
      }`;
    }
    return `@keyframes ssl-w-${i} {
      0%, ${cp(s)}% { opacity: 0; transform: translateY(14px); }
      ${cp(s + 4)}% { opacity: 1; transform: translateY(-2px); }
      ${cp(s + 8)}% { opacity: 1; transform: translateY(0); }
      ${cp(HOLD_END_PCT)}% { opacity: 1; transform: translateY(0); }
      ${cp(FADE_END_PCT)}% { opacity: 0; transform: translateY(-6px); }
      100% { opacity: 0; transform: translateY(-6px); }
    }`;
  }).join('\n');

  let wordIndex = 0;
  return (
    <div
      className="relative mx-auto"
      style={{
        transform: 'translateX(4.5%)',
        width: HERO_MAX_WIDTH,
        // CTV-675: reserve the hero's box before the chrome image loads so the
        // page never jumps and the percentage-positioned screen overlay is
        // correct pre-load.
        aspectRatio: `${TV_NATURAL_W} / ${TV_NATURAL_H}`,
      }}
    >
      <style>{wordKeyframesCss}</style>
      {/* Soft glow behind the TV */}
      <div
        aria-hidden
        className="absolute inset-0 blur-3xl opacity-50 -z-10"
        style={{
          background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.35)}, transparent 70%)`,
        }}
      />
      {/* TV chrome: self-hosted asset in public/ (bezel + stand), originally
          sourced from the ambioedu theme image and preloaded from index.html
          (CTV-675). Its natural size must match TV_NATURAL_W/H because the
          screen-overlay insets below are calibrated to this exact image. The
          screen area is overlaid via the absolutely positioned wrapper below. */}
      <img
        src="/tv-chrome.webp"
        alt=""
        aria-hidden
        width={TV_NATURAL_W}
        height={TV_NATURAL_H}
        fetchPriority="high"
        className="block w-full h-auto relative"
      />
      {/* Screen overlay — positioned over the TV image's screen rectangle.
          Tweak these percentages if the image's screen bounds shift. */}
      <div
        className="absolute overflow-hidden"
        style={{
          // CTV-567: insets are PURE percentages so the screen rectangle scales
          // with the TV. The previous `calc(% - Npx)` nudges were only correct at
          // full size — the fixed px became a larger fraction as the TV shrank,
          // drifting the gradient off the bezel (overhang right, gap at bottom).
          // Each px was folded into its % equivalent at the calibrated full size
          // (888×529): -2px→0.38%h, -1px→0.11%w, -8px→0.90%w, +1px→0.19%h.
          top: '0.62%',
          left: '1.89%',
          right: '10.1%',
          bottom: '16.19%',
          background: `linear-gradient(135deg, ${tintFromAccent(primary, 0.95)}, ${tintFromAccent(accent, 0.85)})`,
          // make the screen a query container so everything on it sizes in cqw
          // (% of the screen's own width) and scales with the TV, not the browser
          // viewport. 100cqw === this screen's width.
          containerType: 'inline-size',
        }}
      >
        {/* CTV-610: per-station background video — fills the screen rectangle,
            muted + looping as ambient wallpaper. The mp4 already has the baked
            "clean first 3s, then blur + dim" treatment, so it plays as-is. When
            the station has no rendered video, we render nothing here and the
            accent gradient above is the fallback. */}
        {backgroundVideoUrl && (
          <video
            aria-hidden
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={() => setVideoReady(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            src={backgroundVideoUrl}
          />
        )}
        {/* Scanline / bloom overlay */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.25), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(0,0,0,0.15), transparent 60%)',
          }}
        />
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Cycling slides on screen */}
          <div className="absolute inset-0 flex items-center justify-center px-[4.1cqw] text-center pointer-events-none">
            <h2
              className="absolute font-semibold tracking-tight text-white leading-[1.6]"
              style={{
                fontSize: 'clamp(20px, 8.3cqw, 64px)',
                textShadow: '0 2px 16px rgba(0,0,0,0.3)',
              }}
            >
              {headlineLines.map((line, lineIdx) => (
                <div key={lineIdx}>
                  {line.split(' ').map((word, wIdx, lineArr) => {
                    const globalI = wordIndex++;
                    const isImpactWord = wordMeta[globalI].isImpact;
                    const shouldUnderline = isImpactWord;
                    return (
                      <React.Fragment key={wIdx}>
                        <span
                          className="relative inline-block"
                          style={{
                            animation: `ssl-w-${globalI} ${cycleSec}s ease-out infinite`,
                            ...(isImpactWord && {
                              color: '#ef4444',
                              fontStyle: 'italic',
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                              textShadow:
                                '0 0 28px rgba(239, 68, 68, 0.65), 0 0 8px rgba(239, 68, 68, 0.4), 0 2px 16px rgba(0,0,0,0.3)',
                            }),
                          }}
                        >
                          {word}
                          {shouldUnderline && (
                            <svg
                              aria-hidden
                              className="absolute"
                              style={{
                                left: 0,
                                right: 0,
                                bottom: '0.15em',
                                width: '100%',
                                height: '0.26em',
                                overflow: 'visible',
                                opacity: 1,
                                transform: 'scaleX(0)',
                                transformOrigin: 'left',
                                animation: `ssl-underline-punch ${cycleSec}s ease-out infinite`,
                              }}
                              viewBox="0 0 100 10"
                              preserveAspectRatio="none"
                            >
                                <path
                                  d="M 2 6 Q 50 3 98 6"
                                  stroke="currentColor"
                                  strokeWidth="2.8"
                                  strokeLinecap="round"
                                  fill="none"
                                  vectorEffect="non-scaling-stroke"
                                />
                              </svg>
                            )}
                          </span>
                          {wIdx < lineArr.length - 1 ? ' ' : ''}
                        </React.Fragment>
                      );
                  })}
                </div>
              ))}
            </h2>
            <div
              className="absolute max-w-[92%] text-center text-white"
              style={{
                animation: `ssl-tv-slide-2 ${cycleSec}s ease-in-out infinite`,
              }}
            >
              <div
                className="font-bold leading-[1.15]"
                style={{
                  fontSize: 'clamp(14px, 5.7cqw, 44px)',
                  textShadow: '0 2px 18px rgba(0,0,0,0.4)',
                }}
              >
                {subtitle}
              </div>
              {devices.length > 0 && (
                <div className="flex flex-wrap items-stretch justify-center gap-[1.5cqw] mt-[2.6cqw]">
                  {devices.map(({ value, label, Icon }, i) => (
                    <span
                      key={value}
                      className="inline-flex flex-col items-center justify-center gap-[0.8cqw] w-[12.4cqw] px-[1cqw] py-[1.3cqw] rounded-2xl text-white"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.33)',
                        border: '1px solid rgba(255,255,255,0.24)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        animation: `ssl-chip-pop ${cycleSec}s ease-out infinite`,
                        animationDelay: `${i * 0.14}s`,
                      }}
                    >
                      <Icon className="w-[4.1cqw] h-[4.1cqw] shrink-0" />
                      {/* One line always: the row shares one font size, scaled
                          down from 1.7cqw only when its longest label (e.g.
                          "Samsung TV") would otherwise wrap in the 12.4cqw
                          pill. Rows of short labels keep the full size. */}
                      <span
                        className="font-semibold leading-none whitespace-nowrap"
                        // data-label-cqw is the test hook: jsdom drops clamp()
                        // from inline styles, so specs assert the shared size
                        // through this attribute instead.
                        data-label-cqw={deviceLabelCqw}
                        style={{ fontSize: `clamp(8px, ${deviceLabelCqw}cqw, 13px)` }}
                      >
                        {label}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Slide 3 — "Your ad here" placeholder */}
            <div
              className="absolute flex flex-col items-center"
              style={{
                animation: `ssl-tv-slide-3 ${cycleSec}s ease-in-out infinite`,
              }}
            >
              <div
                className="relative flex flex-col items-center gap-[1cqw] px-[5.2cqw] py-[3.1cqw] rounded-2xl"
                style={{
                  border: '2px dashed rgba(255,255,255,0.55)',
                  backgroundColor: 'rgba(255,255,255,0.33)',
                }}
              >
                <div
                  className="font-bold uppercase text-white"
                  style={{
                    fontSize: 'clamp(16px, 6.5cqw, 50px)',
                    letterSpacing: '0.14em',
                    textShadow: '0 2px 18px rgba(0,0,0,0.4)',
                  }}
                >
                  Your Ad Here
                </div>
                <div
                  className="font-medium text-white/85"
                  style={{
                    fontSize: 'clamp(11px, 2.6cqw, 20px)',
                    letterSpacing: '0.03em',
                  }}
                >
                  {[
                    { word: 'Built', delay: 12.0 },
                    { word: 'for', delay: 12.15 },
                    { word: 'you.', delay: 12.3 },
                    { word: 'In', delay: 12.6 },
                    { word: 'minutes.', delay: 12.75 },
                  ].map((w, i, arr) => (
                    <React.Fragment key={i}>
                      <span
                        className="inline-block"
                        style={{
                          opacity: 0,
                          animation: `ssl-slide3-word ${cycleSec}s ease-out infinite`,
                          animationDelay: `${w.delay}s`,
                        }}
                      >
                        {w.word}
                      </span>
                      {i < arr.length - 1 ? ' ' : ''}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Channel bug — bottom right watermark, on a scrim matching the
              scene-2 platform chips so the logo stays legible over the video.
              The scrim + logo stamp in together (animation on the wrapper). */}
          {logoUrl && (
            <div
              className="absolute bottom-[1.8cqw] right-[1.8cqw] inline-flex items-center justify-center rounded-2xl px-[1.5cqw] py-[1.2cqw] pointer-events-none"
              style={{
                backgroundColor: 'rgba(255,255,255,0.33)',
                border: '1px solid rgba(255,255,255,0.24)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                animation: `ssl-bug-stamp ${cycleSec}s ease-out infinite`,
                transformOrigin: 'bottom right',
              }}
            >
              <img
                src={logoUrl}
                alt={logoAlt}
                // Normalize footprint across wildly different logo shapes: fit
                // inside a fixed box (object-contain). A square/circle logo
                // (e.g. WISC) fills the height; a wide horizontal logo (e.g.
                // KITV) is reined in by the width cap instead of running long.
                className="max-h-[7.2cqw] max-w-[15cqw] w-auto object-contain opacity-95 drop-shadow-md"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfServiceLanding;
